import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PaymentMethod, isDirectPayment } from 'src/app/helpers/payment-preferences';
import { CartService } from 'src/app/services/cart.service';
import { DialogConfig, DialogGenericComponent } from '../dialog-generic/dialog-generic.component';

@Component({
  selector: 'app-dialog-pay',
  templateUrl: './dialog-pay.component.html',
  styleUrls: ['./dialog-pay.component.scss'],
})
export class DialogPayComponent {
  paymentMethod: PaymentMethod | null = null;
  cashAmount: number | null = null;
  loading = false;
  errorMessage = '';
  // Cobro directo en curso: se queda en la selección de método, sin pantalla intermedia
  directPaying = false;
  readonly direct: Record<PaymentMethod, boolean> = {
    cash: isDirectPayment('cash'),
    tpv: isDirectPayment('tpv'),
  };
  readonly directWarning = this.buildDirectWarning();
  cartTotal: number;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<DialogPayComponent>,
    private cartService: CartService,
    private dialog: MatDialog,
  ) {
    this.cartTotal =
      this.cartService.getCartState()?.total_order?.total ??
      this.cartService.getPriceCart().total;
  }

  get change(): number {
    if (this.cashAmount == null || this.cashAmount < this.cartTotal) return 0;
    return Math.round((this.cashAmount - this.cartTotal) * 100) / 100;
  }

  get cashValid(): boolean {
    return this.cashAmount != null && this.cashAmount >= this.cartTotal;
  }

  selectMethod(method: PaymentMethod): void {
    if (this.loading) return;
    this.paymentMethod = method;
    this.cashAmount = null;
    this.errorMessage = '';

    if (this.direct[method]) {
      this.directPaying = true;
      // Sin importe recibido: se da por entregado el total exacto, cambio 0
      if (method === 'cash') this.cashAmount = this.cartTotal;
      this.confirm();
    }
  }

  private buildDirectWarning(): string | null {
    if (this.direct.cash && this.direct.tpv) {
      return 'Al pulsar un método de pago se completará el pago, sin más confirmaciones.';
    }
    if (this.direct.cash) {
      return 'Al pulsar «Pagar con efectivo» se completará el pago, sin más confirmaciones.';
    }
    if (this.direct.tpv) {
      return 'Al pulsar «Pagar con tarjeta» se completará el pago, sin más confirmaciones.';
    }
    return null;
  }

  private resetDirectPayment(): void {
    if (!this.directPaying) return;
    this.directPaying = false;
    this.paymentMethod = null;
    this.cashAmount = null;
  }

  confirm(): void {
    if (!this.paymentMethod) return;
    if (this.paymentMethod === 'cash' && !this.cashValid) return;

    this.loading = true;
    this.errorMessage = '';

    const payloadForm = {
      cash: this.paymentMethod === 'cash' ? this.cashAmount : 0,
      change: this.paymentMethod === 'cash' ? this.change : 0,
    };

    this.cartService
      .confirmOrder(this.paymentMethod, payloadForm)
      .then(() => {
        this.loading = false;
        this.cartService.clearStorageData();
        this.cartService.setCartConfirmed({ pay: true });
        this.dialogRef.close({ success: true });
      })
      .catch((err) => {
        this.loading = false;
        if (err?.status === 409) {
          this.dialogRef.close();
          this.cartService.emptyCart().subscribe(() => {
            this.cartService.getCart().subscribe();
          });
          this.openErrorGenericDialog(
            'El pedido no se ha realizado',
            'Las entradas ya no están disponibles',
          );
        } else {
          this.resetDirectPayment();
          this.errorMessage =
            err?.error?.message ||
            'No se ha podido procesar el pago. Inténtalo de nuevo.';
        }
      });
  }

  cancel(): void {
    this.dialogRef.close({ success: false });
  }

  private openErrorGenericDialog(title: string, subtitle: string): void {
    const config: DialogConfig = {
      title,
      subtitle,
      icon: 'error',
      buttons: [
        {
          label: 'Aceptar',
          type: 'primary',
          action: () => this.dialog.closeAll(),
        },
      ],
    };
    this.dialog.open(DialogGenericComponent, { data: config });
  }
}
